/** Utilidades mínimas para construir el DOM sin dependencias. */

export type El<K extends keyof HTMLElementTagNameMap> = HTMLElementTagNameMap[K];

export type ElProps<K extends keyof HTMLElementTagNameMap> = Omit<
  Partial<HTMLElementTagNameMap[K]>,
  'className' | 'dataset'
> & {
  className?: string;
  dataset?: Record<string, string>;
  attrs?: Record<string, string>;
};

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  props: ElProps<K> = {} as ElProps<K>,
  children: Array<Node | string | null | undefined> = []
): El<K> {
  const node = document.createElement(tag);
  const { className, dataset, attrs, ...rest } = props as Record<string, unknown>;
  for (const [key, value] of Object.entries(rest)) {
    if (value === undefined || value === null) continue;
    // @ts-expect-error asignación dinámica de propiedades DOM
    node[key] = value;
  }
  if (typeof className === 'string') node.className = className;
  if (dataset) {
    for (const [k, v] of Object.entries(dataset)) node.dataset[k] = v;
  }
  if (attrs) {
    for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, v);
  }
  for (const child of children) {
    if (child === null || child === undefined) continue;
    node.append(child);
  }
  return node as El<K>;
}

export function clear(node: HTMLElement): void {
  while (node.firstChild) node.removeChild(node.firstChild);
}

export function button(
  label: string,
  onClick: () => void,
  opts: { className?: string; title?: string; ariaLabel?: string; disabled?: boolean } = {}
): HTMLButtonElement {
  const b = el('button', {
    className: opts.className ?? 'ae-btn',
    textContent: label,
    type: 'button'
  });
  if (opts.title) b.title = opts.title;
  if (opts.ariaLabel) b.setAttribute('aria-label', opts.ariaLabel);
  if (opts.disabled) b.disabled = true;
  b.addEventListener('click', onClick);
  return b;
}

/** Registra un listener y devuelve una función para eliminarlo. */
export function on<K extends keyof HTMLElementEventMap>(
  target: EventTarget,
  type: K,
  handler: (ev: HTMLElementEventMap[K]) => void,
  options?: AddEventListenerOptions
): () => void {
  target.addEventListener(type, handler as EventListener, options);
  return () => target.removeEventListener(type, handler as EventListener, options);
}
